import { Alert } from 'antd';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceWrapper, AppButton, AppModal, Credit, Flex, LoadingSpin, ReservePriceIcon, TryAgain } from '@/components';
import DailySellListItem from './DailySaleListItem';
import foodImg from '@/assets/images/food.png';
import sportImg from '@/assets/images/swiper-sport.svg';
import clinicImg from '@/assets/images/swiper-clinic-item.svg';
import transportImg from '@/assets/images/swiper-transport-item.svg';
import {
  Swiper,
  SwiperSlide,
  SwiperOutNav,
  Navigation,
  Pagination,
  getDefaultNavigation
} from '@/components/common/Swiper';
import ForgetCardModal from '../../modals/ForgetCardModal';
import { envConfig } from '@/configs';

import './style.scss';

export interface Props {
  variant: ComponentVariant;
  loading: boolean;
  dailySellPrograms: DailySaleProgramEntity[];
  errorMsg: string;
  buyModalVisible: boolean;
  onSelect: (value: boolean) => void;
  setForgetModalVisible: (value: boolean) => void;
  onPressBuy: (id: number, count: number) => void;
  buyLoading: boolean;
  forgetModalVisible: boolean;
  getDailySellList: () => void;
}

const getImage = (variant: ComponentVariant) => ({
  nurture: foodImg,
  sport: sportImg,
  health: clinicImg,
  transport: transportImg,
}[variant]);

const DailySellBody: FC<Props> = (props) => {
  const [selectedProgram, setSelectedProgram] = useState<DailySaleProgramEntity>();
  const [forgetItem, setForgetItem] = useState<DailySaleProgramEntity>();
  const [count, setCount] = useState<number>(0);
  const [t] = useTranslation(['common', 'reserve']);

  const onSelectBuy = (item: DailySaleProgramEntity, count: number) => {
    props.onSelect(true);
    setSelectedProgram(item);
    setCount(count);
  };

  const onRequestForgetModal = (item: DailySaleProgramEntity) => {
    props.setForgetModalVisible(true);
    setForgetItem(item);
  };

  return (
    <div className={`dailysale-container dailysale-theme-${props.variant} relative rtl`}>
      {props.loading ? (
        <LoadingSpin />
      ) : props.errorMsg ? (
        <TryAgain text={props.errorMsg} onRetry={props.getDailySellList} />
      ) : props.dailySellPrograms?.length ? (
        <>
          <DeviceWrapper show={['mobile']}>
            <Swiper
              autoHeight
              slidesPerView={1}
              centeredSlides={true}
              className="swiper-daily-sale"
              pagination={{ enabled: true }}
              direction={'horizontal'}
              modules={[Navigation, Pagination]}
              style={{ width: '100vw', height: 450 }}
              scrollbar={{ draggable: true }}
            >
              {props.dailySellPrograms.map((product, index) => (
                <SwiperSlide key={index} className="justify-center rtl">
                  <DailySellListItem
                    item={product}
                    onIncrement={onSelectBuy}
                    onRequestForget={onRequestForgetModal}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </DeviceWrapper>
          <DeviceWrapper show={['desktop']}>
            <SwiperOutNav id="daily-sell" />
            <Swiper
              autoHeight
              slidesPerView={3}
              className="swiper-daily-sale relative"
              modules={[Navigation, Pagination]}
              navigation={getDefaultNavigation('daily-sell')}
            >
              {props.dailySellPrograms.map((product, index) => (
                <SwiperSlide key={index} className="justify-center">
                  <DailySellListItem
                    item={product}
                    onIncrement={onSelectBuy}
                    onRequestForget={onRequestForgetModal}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </DeviceWrapper>
        </>
      ) : (
        <Alert type="warning" message={t('reserve:messages.no-daily-sale-for-today')} />
      )}
      {props.buyModalVisible && (
        <AppModal
          footer={null}
          variant={props.variant}
          title={t(`reserve:context.reserve-title.${props.variant}`)}
          className={`dailysale-modal-container dailysale-theme-${props.variant}`}
          open={props.buyModalVisible}
          onCancel={() => props.onSelect(false)}
        >
          <div className="rtl w-full p-2">
            <div className="flex items-center md:items-start">
              <div className="dailysale-modal-text-container">
                <div className="dailysale-modal-text font-bold">
                  {selectedProgram?.mealTypeName}
                </div>
                <div className="dailysale-modal-text">
                  {selectedProgram?.selfName}
                </div>
                <div className="dailysale-modal-text">
                  {selectedProgram?.foodTypeTitle}
                </div>
              </div>
              <img className="dailysale-modal-img" src={getImage(props.variant)} alt="Image" />
            </div>
            <div className="mt-3">
              <div className="dailysale-item-food">
                {t(`reserve:context.item-name-prefix.${props.variant}`)}
                {selectedProgram?.foodNames.split('|')[0]}
              </div>
              <div className="dailysale-item-besideFood !text-[#979797]">
                {selectedProgram?.foodNames.split('|')[1]}
              </div>
              {!envConfig.ui.removePrices && (
                <div className="flex items-center justify-start mt-2">
                  <div className="mx-1" />
                  <Flex align="center" justify="flex-end" className="ms-auto w-full">
                    <ReservePriceIcon className="text-app-orange" />
                    <Credit
                      inline
                      numColor="#9B9999"
                      postfixColor="#9B9999"
                      customValue={selectedProgram?.price || 0}
                    />
                  </Flex>
                </div>
              )}
              <AppButton
                loading={props.buyLoading}
                appVariant="nurture"
                className="mt-4 !flex !w-full items-center justify-center"
                onClick={() => props.onPressBuy(selectedProgram?.id || 0, count)}
              >
                {t('reserve:labels.reserve')}
              </AppButton>
              <div />
            </div>
          </div>
        </AppModal>
      )}
      {props.forgetModalVisible && (
        <ForgetCardModal
          item={forgetItem}
          dailySale={true}
          variant={props.variant}
          open={props.forgetModalVisible}
          hide={() => props.setForgetModalVisible(false)}
        />
      )}
    </div>
  );
};

export default DailySellBody;
